/**
 * Redux witness watchers for iamges
 * @module services/images/watchers
 */
import { Map, List } from 'immutable'
import { getLocationPathname } from 'services/location'
import {
  GET_DATA_LIST,
  setDataList as setDataListAction,
  resetData as resetDataAction,
  setDataList,
  setDataApplication,
  setDataApplicationByKey,
} from 'services/data/redux/actions'
import {
  CREATE_IMAGES, CREATE_IMAGE, DELETE_IMAGE, DROP_IMAGES,
} from 'services/images/redux/actions'
import {
  renderModal as renderModalAction,
  dismissModal as dismissModalAction,
} from 'services/modal/redux/actions'
import { FETCHING_MODAL } from 'services/modal'
import { getConfigDataServer } from 'services/config'
import { createViewRecords, getViewRecords } from 'services/data'
import { getContentList, isContentDetailPage } from 'services/content'
import { getContentReferencesList, filterByContentReferenceType } from 'services/content-references'
import { getContentTypes, getD6ContentType } from 'services/content/d6-content-types'
import { CONTENT_REFERENCE_TYPE_SERIES } from 'services/content-references/types'
import { getExponentialDelay } from 'exponential-backoff'
import { delay, map as mapPromise } from 'bluebird'
import { showErrorToast, showSuccessToast } from 'services/toast/redux/actions'
import { getFirstErrorMessage } from 'services/error'
import {
  getImageContentRelationshipList,
  getImageContentRelationshipByD6IdAndD6ContentType,
  createImageContentRelationshipUnique,
  createImage,
  deleteImage,
  isValidImageContentRelationship,
} from '.'
import { STORE_KEY_IMAGES } from './store-keys'

/**
 * Watcher to listen for the set data list action on image upload bulk page and load dependancies
 */
export function onImageUploadBulkGetDataList ({ after }) {
  return after(DROP_IMAGES, async ({ state, action, dispatch }) => {
    const { auth, config } = state
    const { payload: { options, application } } = action

    const files = options.get('files', [])
    let applicationData = application

    await mapPromise(files, async (imageData) => {
      const contentId = imageData.getIn(['contentRelationship', 'd6Id'])
      const possibleContentTypes = getContentTypes(imageData.getIn(['contentRelationship', 'd6ContentType']))
      const possibleContent = possibleContentTypes.map(
        (contentType) => Map({ contentId, contentType }),
      )
      const language = imageData.getIn(['contentRelationship', 'language'])
      const imageId = imageData.get('id')
      const fileBlob = imageData.get('fileBlob')
      let isSource = imageData.getIn(['contentRelationship', 'isSource'])
      let dataUri

      const fileReader = new FileReader()
      fileReader.onload = async (fileEvent) => {
        const { target: { result } } = fileEvent
        dataUri = result
      }
      fileReader.readAsDataURL(fileBlob)

      // Check content information, but don't fail if not found
      let contentList = Map()
      try {
        contentList = await getContentList({
          auth,
          server: getConfigDataServer(config),
          content: possibleContent.toJS(),
          language,
        })
      } catch (e) {
        dispatch(showErrorToast(getFirstErrorMessage(e)))
      }

      // Check content information, but don't fail if not found
      let contentReferencesList = Map()
      try {
        if (!contentList.get('content', List()).isEmpty()) {
          contentReferencesList = await getContentReferencesList({
            auth,
            server: getConfigDataServer(config),
            contentReferences: contentList.get('content', List()).map((v) => Map({ fromContentContentId: v.get('contentId'), fromContentContentType: v.get('contentType') })).toJS(),
            language,
          })
        }
      } catch (e) {
        dispatch(showErrorToast(getFirstErrorMessage(e)))
      }

      try {
        const seriesContentReferences = filterByContentReferenceType(
          contentReferencesList.get('contentReferences', List()),
          List([CONTENT_REFERENCE_TYPE_SERIES]),
        )
        // Check content information, but don't fail if not found
        if (!seriesContentReferences.isEmpty()) {
          const contentSeriesList = await getContentList({
            auth,
            server: getConfigDataServer(config),
            content: seriesContentReferences.map((v) => ({
              contentId: v.get('toContentContentId'),
              contentType: v.get('toContentContentType'),
            })).toJS(),
            language,
          })
          contentList = contentList.merge(Map({
            totalCount: contentList.get('totalCount', 0) + 1,
            content: contentList.get('content', List()).push(contentSeriesList.get('content', List()).first()),
          }))
        }
      } catch (e) {
        dispatch(showErrorToast(getFirstErrorMessage(e)))
      }

      const firstContentItem = contentList.get('content', List()).first()

      if (firstContentItem) {
        isSource = firstContentItem.get('primaryLanguage') === language
      }

      const contentRelationship = createImageContentRelationshipUnique(Map({
        d6Id: imageData.getIn(['contentRelationship', 'd6Id']),
        d6ContentType: imageData.getIn(['contentRelationship', 'd6ContentType']),
        type: imageData.getIn(['contentRelationship', 'type']),
        language,
        isSource,
      }))

      // If we don't have a valid content relationship don't get dependancies
      if (!isValidImageContentRelationship(contentRelationship)) {
        return
      }

      dispatch(setDataList({
        storeKey: 'content',
        options: Map({ image: Map({ contentRelationship }) }),
        processing: true,
      }))
      // Set the image content references
      dispatch(setDataList({
        storeKey: 'contentReferences',
        options: Map({ image: Map({ contentRelationship }) }),
        processing: true,
      }))
      // Set the image content relationships
      dispatch(setDataList({
        storeKey: STORE_KEY_IMAGES,
        options: contentRelationship,
        processing: true,
      }))

      let imageList = List()
      try {
        imageList = await getImageContentRelationshipList({
          auth,
          server: getConfigDataServer(config),
          image: Map({ contentRelationship }),
        })
      } catch (e) {
        dispatch(showErrorToast(getFirstErrorMessage(e)))
      }

      // Set the image content
      dispatch(setDataList({
        storeKey: 'content',
        options: Map({ image: Map({ contentRelationship }) }),
        total: contentList.get('totalCount') || 0,
        records: createViewRecords(contentList.get('content') || List()),
        processing: false,
      }))
      // Set the image content references
      dispatch(setDataList({
        storeKey: 'contentReferences',
        options: Map({ image: Map({ contentRelationship }) }),
        total: contentReferencesList.get('totalCount') || 0,
        records: createViewRecords(contentReferencesList.get('contentReferences') || List()),
        processing: false,
      }))
      // Set the image content relationships
      dispatch(setDataList({
        storeKey: STORE_KEY_IMAGES,
        options: contentRelationship,
        total: imageList.size,
        records: createViewRecords(imageList),
        processing: false,
      }))

      applicationData = applicationData
        .setIn(['bulkUpload', imageId, 'data', 'contentRelationship'], contentRelationship)
        .setIn(['bulkUpload', imageId, 'data', 'url'], dataUri)
        .setIn(['bulkUpload', imageId, 'compressing'], false)

      dispatch(setDataApplication({
        storeKey: STORE_KEY_IMAGES,
        application: applicationData,
      }))
    })
  })
    .when(({ state, action }) => {
      const { payload: { storeKey } = {} } = action
      const { location = Map() } = state
      return storeKey === STORE_KEY_IMAGES && getLocationPathname(location) === '/images/upload-bulk'
    })
}

/**
 * Watcher to listen for the set data list action on image by content page and load dependancies
 */
export function onImageByContentGetDataList ({ after }) {
  return after(GET_DATA_LIST, async ({ state, action, dispatch }) => {
    const { auth, config } = state
    const { payload: { options } } = action
    const contentId = options.get('contentId')
    const contentType = options.get('contentType')
    dispatch(setDataList({
      storeKey: 'content',
      options,
      processing: true,
    }))
    // Set the image content references
    dispatch(setDataList({
      storeKey: 'contentReferences',
      options,
      processing: true,
    }))
    // Set the image content relationships
    dispatch(setDataList({
      storeKey: STORE_KEY_IMAGES,
      options,
      processing: true,
    }))
    let contentList = Map()
    let contentError = null
    try {
      // Check content information, but don't fail if not found
      contentList = await getContentList({
        auth,
        server: getConfigDataServer(config),
        content: [{ contentId, contentType }],
      })
    } catch (e) {
      contentError = e
    }
    let contentReferencesList = Map()
    const contentReferencesError = null
    try {
      // Check content information, but don't fail if not found
      if (!contentList.get('content', List()).isEmpty()) {
        contentReferencesList = await getContentReferencesList({
          auth,
          server: getConfigDataServer(config),
          contentReferences: [{
            fromContentContentId: contentId,
            fromContentContentType: contentType,
          }],
        })
      }
    } catch (e) {
      // Do nothing
    }
    try {
      const seriesContentReferences = filterByContentReferenceType(
        contentReferencesList.get('contentReferences', List()),
        List([CONTENT_REFERENCE_TYPE_SERIES]),
      )
      // Check content information, but don't fail if not found
      if (!seriesContentReferences.isEmpty()) {
        const contentSeriesList = await getContentList({
          auth,
          server: getConfigDataServer(config),
          content: seriesContentReferences.map((v) => ({
            contentId: v.get('toContentContentId'),
            contentType: v.get('toContentContentType'),
          })).toJS(),
        })
        contentList = contentList.merge(Map({
          totalCount: contentList.get('totalCount', 0) + 1,
          content: contentList.get('content', List()).push(contentSeriesList.get('content', List()).first()),
        }))
      }
    } catch (e) {
      // Do nothing
    }
    let imageList = List()
    let imageError = null
    try {
      imageList = await getImageContentRelationshipList({
        auth,
        server: getConfigDataServer(config),
        image: Map({
          contentRelationship: Map({
            d6Id: contentId,
            d6ContentType: getD6ContentType(contentType),
          }),
        }),
      })
    } catch (e) {
      imageError = e
    }
    // Set the image content
    dispatch(setDataList({
      storeKey: 'content',
      options,
      error: contentError,
      total: contentList.get('totalCount') || 0,
      records: createViewRecords(contentList.get('content') || List()),
      processing: false,
    }))
    // Set the image content references
    dispatch(setDataList({
      storeKey: 'contentReferences',
      options,
      error: contentReferencesError,
      total: contentReferencesList.get('totalCount') || 0,
      records: createViewRecords(contentReferencesList.get('contentReferences') || List()),
      processing: false,
    }))
    // Set the image content relationships
    dispatch(setDataList({
      storeKey: STORE_KEY_IMAGES,
      options,
      error: imageError,
      total: imageList.size,
      records: createViewRecords(imageList),
      processing: false,
    }))
  })
    .when(({ state, action }) => {
      const { payload: { storeKey } = {} } = action
      const { location = Map() } = state
      return storeKey === STORE_KEY_IMAGES && getLocationPathname(location) === '/images/manage-by-content'
    })
}

export function getImages ({ after }) {
  return after(GET_DATA_LIST, async ({ action, state, dispatch }) => {
    const { auth, config, data } = state
    const { storeKey, options } = action.payload
    const images = data.get('images')
    if (images) {
      dispatch(resetDataAction({ storeKey }))
    }
    dispatch(setDataListAction({
      storeKey,
      options,
      processing: true,
    }))
    let imageContentRelationships = []
    try {
      imageContentRelationships = await getImageContentRelationshipByD6IdAndD6ContentType({
        auth,
        server: getConfigDataServer(config),
        d6Id: options.get('contentId'),
        d6ContentType: getD6ContentType(options.get('contentType')),
        language: options.get('language'),
      })
    } catch (e) {
      // do nothing
    }
    dispatch(setDataListAction({
      storeKey,
      options,
      records: createViewRecords(imageContentRelationships),
      processing: false,
    }))
  })
    .when(({ action, state }) => isImageDataAction({ action }) && isContentDetailPage(state))
}

/**
 * Returns true if the action has a storeKey of 'images'
 * @param {Object} param0 The parameters
 * @param {Object} [param0.action={}] A redux action
 * @param {Object} [param0.action.payload] The action payload
 * @param {String} [param0.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals images
 */
function isImageDataAction ({ action = {} } = {}) {
  const { payload: { storeKey } = {} } = action
  return storeKey === STORE_KEY_IMAGES
}

/**
 * Create images in the provide by the action application property using the application key
 * @param {Object} param0 The params
 */
export function onCreateImages ({ after }) {
  return after(CREATE_IMAGES, async ({ action, dispatch, state }) => {
    const { config, auth } = state
    const { payload: { storeKey, applicationKey } = {} } = action
    let { payload: { application = Map() } = {} } = action
    application = application.withMutations(
      (mutatorItem) => mutatorItem.updateIn(
        [applicationKey],
        Map(),
        (updaterItem) => updaterItem.map((v) => {
          let item = v.set('creating', true)
          // reset all existed images error
          item = v.set('error', null)
          return item
        }),
      ),
    )
    dispatch(setDataApplication({ storeKey, application }))
    /**
     * Create an image with retry using the API
     * @param {Map} imageContainer The image from the store
     * @param {Number} [attempt=0] The current attempt
     */
    async function createImageWithRetry (image, attempt, maxAttempt = 10) {
      try {
        return createImage({
          image,
          auth,
          server: getConfigDataServer(config),
          processImage: true,
        })
      } catch (e) {
        if (attempt < maxAttempt) {
          await delay(getExponentialDelay(attempt))
          return createImageWithRetry(image, attempt + 1, maxAttempt)
        }
        throw e
      }
    }

    dispatch(
      renderModalAction(FETCHING_MODAL, {
        innerClass: 'modal__fetching',
        backgroundDismiss: false,
        hideDismiss: true,
        title: 'Uploading images',
      }),
    )

    // Create [key, value] tuples for bluebird to map over
    const imagesTuples = application.get(applicationKey, Map()).entries()
    await mapPromise(imagesTuples, async ([imageApplicationKey, imageApplicationData] = []) => {
      const originalFileName = imageApplicationData.getIn(['data', 'originalFileName'])

      try {
        const contentRelationship = imageApplicationData.getIn(['data', 'contentRelationship'], Map())

        // Only create the image if we don't have a content relationship or it is valid
        if (isValidImageContentRelationship(contentRelationship)) {
          const imageContentRelationship = await createImageWithRetry(imageApplicationData.get('data'))

          // Set the image content references
          dispatch(setDataList({
            storeKey: STORE_KEY_IMAGES,
            options: createImageContentRelationshipUnique(contentRelationship),
            error: null,
            total: 1,
            records: createViewRecords(List([imageContentRelationship])),
          }))

          dispatch(setDataApplicationByKey({
            storeKey,
            applicationKey,
            applicationData: application.get(applicationKey).delete(imageApplicationKey),
          }))

          dispatch(showSuccessToast(`${originalFileName} was successfully uploaded`))
        }
      } catch (e) {
        dispatch(showErrorToast(getFirstErrorMessage(e)))

        dispatch(setDataApplicationByKey({
          storeKey,
          applicationKey,
          applicationData: application.get(applicationKey).update(
            imageApplicationKey,
            (value) => value.set('creating', false),
          ),
        }))
      }
    }, { concurrency: 20 })

    dispatch(dismissModalAction())
  })
}

/**
 * Delete an image with retry using the API
 * @param {String} imageId The image id to delete
 * @param {Number} [attempt=0] The current attempt
 */
async function deleteImageWithRetry (config, auth, imageId, attempt, maxAttempt = 10) {
  try {
    return await deleteImage({
      image: Map({ id: imageId }),
      auth,
      server: getConfigDataServer(config),
      processImage: true,
    })
  } catch (err) {
    if (attempt < maxAttempt) {
      await delay(getExponentialDelay(attempt))
      return deleteImageWithRetry(config, auth, imageId, attempt + 1, maxAttempt)
    }
    throw err
  }
}

/**
 * Delete one image updating application property by the application key
 * @param {Object} param0 Witness params
 */
export function onDeleteImage ({ after }) {
  return after(DELETE_IMAGE, async ({ action, dispatch, state }) => {
    const { config, auth, data } = state
    const {
      payload: {
        storeKey, imageApplicationKey, applicationKey, imageId, imageView,
      } = {},
    } = action
    let { payload: { imageApplicationData } = {} } = action
    if (!imageApplicationData.isEmpty()) {
      imageApplicationData = imageApplicationData.set('deleting', true)
      dispatch(setDataApplicationByKey({
        storeKey,
        applicationKey,
        applicationData: Map({ [imageApplicationKey]: imageApplicationData }),
      }))
    }

    try {
      await deleteImageWithRetry(config, auth, imageId)
      // Set the image content references
      const records = getViewRecords({
        data,
        storeKey: STORE_KEY_IMAGES,
        options: imageView.get('options'),
      }).filter((_, k) => k !== imageId)
      dispatch(setDataList({
        storeKey: STORE_KEY_IMAGES,
        options: imageView.get('options'),
        error: null,
        total: records.size,
        records,
      }))
    } catch (e) {
      // Do nothing
    }
    if (!imageApplicationData.isEmpty()) {
      imageApplicationData = imageApplicationData.set('deleting', false)
      dispatch(setDataApplicationByKey({
        storeKey,
        applicationKey,
        applicationData: Map({ [imageApplicationKey]: imageApplicationData }),
      }))
    }
  })
}

/**
 * Create one image updating application property by the application key
 * @param {Object} param0 Witness params
 */
export function onCreateImage ({ after }) {
  return after(CREATE_IMAGE, async ({ action, dispatch, state }) => {
    const { config, auth } = state
    const { payload: { storeKey, imageApplicationKey, applicationKey } = {} } = action
    let { payload: { imageApplicationData } = Map() } = action
    imageApplicationData = imageApplicationData.set('creating', true)
    dispatch(setDataApplicationByKey({
      storeKey,
      applicationKey,
      applicationData: Map({ [imageApplicationKey]: imageApplicationData }),
    }))

    /**
     * Create an image with retry using the API
     * @param {Map} image The image to create
     * @param {Number} [attempt=0] The current attempt
     */
    async function createImageWithRetry (image, attempt, maxAttempt = 10) {
      try {
        return await createImage({
          image,
          auth,
          server: getConfigDataServer(config),
          processImage: true,
        })
      } catch (err) {
        if (attempt < maxAttempt) {
          await delay(getExponentialDelay(attempt))
          return createImageWithRetry(image, attempt + 1, maxAttempt)
        }
        throw err
      }
    }
    try {
      let contentRelationship = imageApplicationData.getIn(['data', 'contentRelationship'])
      // Only create the image if we don't have a content relationship or it is valid
      if (!contentRelationship || isValidImageContentRelationship(contentRelationship)) {
        const image = await createImageWithRetry(imageApplicationData.get('data'))
        contentRelationship = createImageContentRelationshipUnique(contentRelationship)
        // REplace the image data with the API response
        imageApplicationData = imageApplicationData.set('data', image)
        // Set the image in the images store
        dispatch(setDataList({
          storeKey: STORE_KEY_IMAGES,
          options: contentRelationship,
          error: null,
          total: 1,
          records: createViewRecords(List([image])),
        }))
      }
    } catch (e) {
      // Do nothing
    }
    dispatch(setDataApplicationByKey({
      storeKey,
      applicationKey,
      applicationData: Map({
        [imageApplicationKey]: imageApplicationData.set('creating', false),
      }),
    }))
  })
}
